import React from 'react';
import {
  Box,
  Popover,
  Typography,
  List,
  ListItem,
  ListItemButton,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Notification } from '../../services/api/notificationApi';
import { formatDistanceToNow } from 'date-fns';

interface NotificationPanelProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  notifications: Notification[];
  loading: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  pageSize?: number;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onDelete: (id: string) => void;
  onRefresh: () => void;
  onLoadMore?: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  anchorEl,
  open,
  onClose,
  notifications,
  loading,
  loadingMore = false,
  hasMore = false,
  pageSize = 10,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  onRefresh,
  onLoadMore,
}) => {
  const theme = useTheme();

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
      case 'calculation_complete':
        return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
      case 'error':
      case 'calculation_error':
        return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
      case 'warning':
        return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
      case 'info':
      default:
        return <InfoIcon sx={{ color: theme.palette.info.main }} />;
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }
    
    // Handle navigation based on notification metadata
    if (notification.metadata?.fileId) {
      // Navigate to calculated files page
      globalThis.location.href = '/files/calculated';
    }
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onDelete(id);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: {
            width: 420,
            maxHeight: 600,
            mt: 1.5,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: `1px solid ${theme.palette.divider}`,
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.primary.main}04)`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Notifications
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={onRefresh} disabled={loading} title="Refresh">
             <RefreshIcon fontSize="small" />
          </IconButton>
          {notifications.some(n => !n.isRead) && (
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={onMarkAllAsRead}
              sx={{ textTransform: 'none' }}
            >
              Mark all read
            </Button>
          )}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
        {(() => {
          if (loading) {
            return (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={32} />
          </Box>
            );
          }
          if (notifications.length === 0) {
            return (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <InfoIcon sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
            );
          }
          return (
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                {index > 0 && <Divider />}
                <ListItem
                  disablePadding
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => handleDelete(e, notification.id)}
                      sx={{
                        opacity: 0.6,
                        '&:hover': { opacity: 1, color: theme.palette.error.main },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      py: 2,
                      px: 2,
                      backgroundColor: notification.isRead
                        ? 'transparent'
                        : `${theme.palette.primary.main}08`,
                      '&:hover': {
                        backgroundColor: notification.isRead
                          ? `${theme.palette.action.hover}`
                          : `${theme.palette.primary.main}12`,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2, width: '100%', pr: 4 }}>
                      <Box sx={{ mt: 0.5 }}>{getNotificationIcon(notification.type)}</Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: notification.isRead ? 400 : 600,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {notification.message}
                          </Typography>
                          {!notification.isRead && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: theme.palette.primary.main,
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(new Date(notification.createdAt), {
                            addSuffix: true,
                          })}
                        </Typography>
                        {notification.metadata?.fileName && (
                          <Chip
                            label={notification.metadata.fileName}
                            size="small"
                            sx={{
                              mt: 1,
                              height: 20,
                              fontSize: '0.7rem',
                              backgroundColor: `${theme.palette.primary.main}15`,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
            {hasMore && onLoadMore && (
              <Box sx={{ p: 1.5, textAlign: 'center', borderTop: `1px solid ${theme.palette.divider}` }}>
                <Button
                  size="small"
                  color="primary"
                  onClick={onLoadMore}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={14} /> : null}
                  sx={{ textTransform: 'none' }}
                >
                  {loadingMore ? 'Loading...' : `Load more`}
                </Button>
              </Box>
            )}
          </List>
          );
        })()}
      </Box>
    </Popover>
  );
};

export default NotificationPanel;
